import type { B2BNavLink } from '../types';

/** True when an href is an absolute URL (has a scheme), e.g. `https://...`. */
const isAbsoluteHref = (href: string): boolean => /^https?:\/\//i.test(href);

/**
 * Resolve a single B2B nav/footer link against the request's main-site origin.
 *
 * - `mainSite` links are prefixed with `mainSiteOrigin` (e.g. `/projects` ->
 *   `https://archiproamerica.com/projects`) so they leave the business
 *   subdomain, and are rendered as plain `<a>` full navigations.
 * - Already-absolute links (e.g. social profiles) are likewise rendered as
 *   plain `<a>` so they don't go through the Remix client router.
 * - Everything else is left untouched as a client-side Remix path on the
 *   subdomain (e.g. the B2B `/professionals` and `/brands` cross-links).
 */
export const resolveB2BLink = (
    link: B2BNavLink,
    mainSiteOrigin: string,
): B2BNavLink => {
    const href = link.mainSite ? `${mainSiteOrigin}${link.href}` : link.href;
    return {
        ...link,
        href,
        isNonRemixRoute: link.isNonRemixRoute || isAbsoluteHref(href),
    };
};

/** Resolve a list of links — see {@link resolveB2BLink}. */
export const resolveB2BLinks = (
    links: B2BNavLink[],
    mainSiteOrigin: string,
): B2BNavLink[] => links.map((link) => resolveB2BLink(link, mainSiteOrigin));
